import React, { useState, useEffect } from "react";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import {
  Divider,
  Menu,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { MenuItem } from "@mui/material";
import { Select } from "@mui/material";
import { Grid } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Tooltip from "@mui/material/Tooltip";
import InputAdornment from "@mui/material/InputAdornment";
import Search from "@mui/icons-material/Search";
import Typography from "@mui/material/Typography";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import WhatsappFormDialog from "./WhatsappFormDialog";
import { TEMPLATE_ENDPOINT } from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import { Add, Category, Language, Translate, Tune } from "@mui/icons-material";
import { FilterModal, FilterModalItem } from "../FilterModal";
import { CalendarIcon } from "@mui/x-date-pickers";
import TableFilterButton from "../TableFilterButton";
import TableSearchInput from "../TableSearchInput";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";
import CampaignIcon from "@mui/icons-material/Campaign";
import SyncIcon from "@mui/icons-material/Sync";
import {
  borderColors,
  ColorsConstants,
  grayFontColor,
} from "../../cssUtils/constants";
import { FiberManualRecord as DotIcon } from "@mui/icons-material";
import CustomPagination from "../../common/custom-pagination";
import AppTable from "../../common/app-table";
import AppDropdown from "../../common/app-dropdown";
import typography from "../../cssUtils/typography";
import {
  FILTER_NAMESPACES,
  loadFilters,
  saveFilters,
  clearFilters,
} from "../../utils/filterStorage";
import { formatWithTzLabel } from "../../utils/dateUtils";

const FILTER_KEY = FILTER_NAMESPACES.SMS_TEMPLATES;
const DEFAULT_TEMPLATE_FILTERS = {
  language: "",
  category: "",
  status: "",
  dateRange: [null, null],
  appliedDateRange: [null, null],
};

const columns = [
  { id: "template_name", label: "Template Name" },
  { id: "category", label: "Category" },
  { id: "language", label: "Language" },
  { id: "status", label: "Status" },
  { id: "template_id", label: "Template ID" },
  { id: "template_quality", label: "Template Quality" },
  { id: "description", label: "Description" },
  { id: "created_at", label: "Created Date" },
  { id: "updated_at", label: "Updated At" },
  { id: "updated_by", label: "Updated By" },
  { id: "action", label: "Action" },
];

const TEMPLATE_QUALITY = {
  GREEN: { color: "#005F02", label: "High" },
  YELLOW: { color: "#FBEF76", label: "Medium" },
  RED: { color: "#C40C0C", label: "Low" },
  UNKNOWN: { color: "#C5C7BC", label: "Unknown" },
};

export default function SMSTemplatesTab({
  refreshKey,
  dateInput,
  setTemplatesNamesData,
  handleCreateTemplateClick,
  setComponent,
  setTemplatesData,
  ...props
}) {
  const { getValidToken } = useAuth();
  const [rowCount, setRowCount] = useState(0);
  const [templatesData, setTemplateData] = useState([]);
  const [languageList, setLanguageList] = useState([]);
  const [templatesModalState, settemplatesModalState] = useState({
    isOpen: false,
    selectedClient: null,
    mode: "add",
    jsonData: "",
  });
  const URL =
    process.env.NODE_ENV === "production" ? "api" : `http://localhost:8000/api`;
  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: 20,
    serverSidePage: 0,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [searchTemplateId, setSearchTemplateId] = useState("");
  const [charCount, setCharCount] = useState(0);

  const captilazeFirstLetter = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const languageOptions = [...new Set(languageList)].map((language) => (
    <MenuItem key={language} value={language}>
      {captilazeFirstLetter(language)}
    </MenuItem>
  ));

  const categoryOptions = [
    ...new Set(templatesData.map((template) => template.category)),
  ].map((category) => (
    <MenuItem key={category} value={category}>
      {captilazeFirstLetter(category)}
    </MenuItem>
  ));

  // Consolidated filter state (persisted to localStorage)
  const [filters, setFilters] = useState(() => loadFilters(FILTER_KEY, DEFAULT_TEMPLATE_FILTERS));
  const {
    language: languageFilter,
    category: categoryFilter,
    status: statusFilter,
    dateRange,
    appliedDateRange,
  } = filters;
  const updateFilter = (field, value) => setFilters((prev) => ({ ...prev, [field]: value }));

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const [dateAnchorEl, setDateAnchorEl] = useState(null);

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [dateError, setDateError] = useState(null);

  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);

  const [syncFlag, setSyncFlag] = useState(false);

  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const fetchData = async (isSync = false) => {
    let searchDateURL = "";
    if (dateInput) {
      let dateString = dayjs(dateInput).format("YYYY-MM-DD");
      searchDateURL = `&search_date=${dateString}`;
    }

    if (dateError) {
      return;
    }

    const queryParams = new URLSearchParams({
      limit: controller.rowsPerPage.toString(),
      offset: (controller.serverSidePage * controller.rowsPerPage).toString(),
      search_query: searchQuery,
      template_id: searchTemplateId,
      language_filter: languageFilter,
      category_filter: categoryFilter,
      status_filter: statusFilter,
      from_date: dateRange[0]
      ? dayjs(dateRange[0]).format("YYYY-MM-DD"): "",
      to_date: dateRange[1]
      ? dayjs(dateRange[1]).format("YYYY-MM-DD"): "",
      sync: isSync.toString(),
      ordering: "-last_edited",
    });

    const token = await getValidToken();

    const tenant = localStorage.getItem("tenant");

    try {
      const response = await fetch(
        `${TEMPLATE_ENDPOINT}?${queryParams}${searchDateURL}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }
      const jsonData = await response.json();

      setTemplateData(jsonData.results);
      setTemplatesNamesData(jsonData.template_names);
      setLanguageList(jsonData.language_list);
      setRowCount(jsonData.count);

      if (isSync) setSyncFlag(false);
    } catch (error) {
      console.error("Error fetching data:", error.message);
      if (isSync) setSyncFlag(false);
    }
  };

  useEffect(() => {
    const hasPending = templatesData.some((t) => t.status === "PENDING");
    if (!hasPending) return;
    const interval = setInterval(() => {
      fetchData(false);
    }, 30000);
    return () => clearInterval(interval);
  }, [templatesData]);

  useEffect(() => {
    fetchData(syncFlag);
  }, [
    controller.serverSidePage,
    controller.rowsPerPage,
    refreshKey,
    searchQuery,
    searchTemplateId,
    languageFilter,
    categoryFilter,
    statusFilter,
    dateRange,
    syncFlag,
  ]);

  const handlePageChange = (event, newPage) => {
    setController((prev) => ({
      ...prev,
      page: newPage,
      serverSidePage: newPage,
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    setController((prev) => ({
      ...prev,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0,
      serverSidePage: 0,
    }));
  };

  const handleDateRangeApply = () => {
    updateFilter("appliedDateRange", dateRange);
    setDateAnchorEl(null);
    setController((prev) => ({ ...prev, page: 0 }));
  };

  const handleDateRangeCancel = () => {
    setFilters((prev) => ({ ...prev, dateRange: [null, null], appliedDateRange: [null, null] }));
    setDateAnchorEl(null);
    if (appliedDateRange[0] && appliedDateRange[1]) {
      setController((prev) => ({ ...prev, page: 0 }));
    }
  };

  const handleSearchQueryChange = (event) => {
    if (event.target.value.length > 50) return;
    const value = event.target.value;
    setSearchQuery(event.target.value);
    setCharCount(value.length);
  };

  const handleSearchByTemplateIDChange = (event) => {
  const value = event.target.value;
  setSearchTemplateId(value);
};

  const handleDisableClick = (template) => {
    if (!template.active) return;
    settemplatesModalState({
      isOpen: true,
      selectedClient: template,
      mode: "disable",
    });
  };

  const handleViewClick = (template, e) => {
    if (!template.active) {
      e.preventDefault();
      return;
    }
    if (template.template_preview_json != "{}") {
      settemplatesModalState({
        isOpen: true,
        selectedClient: template,
        mode: "view",
        jsonData: template.template_preview_json,
      });
    }
  };

  const handleLanguageFilterChange = (event) => {
    updateFilter("language", event.target.value);
  };

  const handleCategoryFilterChange = (event) => {
    updateFilter("category", event.target.value);
  };

  const handleFromDateChange = (newValue) => {
    if (toDate && newValue && dayjs(newValue).isAfter(toDate)) {
      setDateError("From Date must be less than or equal to To Date");
    } else {
      setDateError(null);
      updateFilter("dateRange", [newValue, dateRange[1]]);
    }
  };

  const handleDateClick = (event) => {
    setDateAnchorEl(event.currentTarget);
  };

  const handleDateClose = () => {
    setAnchorEl(null);
  };

  const handleApplyDate = () => {
    if (!dateRange[0] || !dateRange[1]) {
      setDateError("Both dates are required");
      return;
    }
    updateFilter("appliedDateRange", [dateRange[0], dateRange[1]]);
    setDateError(null);
    handleDateClose();
  };

  const handleToDateOpen = () => {
    if (!dateRange[0]) {
      setDateError("Please select From date");
      setEndDatePickerOpen(false);
    } else {
      setDateError(null);
      setEndDatePickerOpen(true);
    }
    setController((prev) => ({ ...prev, page: 0, serverSidePage: 0 }));
  };

  const handleToDateChange = (newValue) => {
    if (dateRange[0] && newValue && dayjs(newValue).isBefore(dateRange[0])) {
      setDateError("To Date must be greater than or equal to From Date");
    } else {
      setDateError(null);
      setToDate(newValue);
    }
  };

  const handleResetFilter = () => {
    setFilters({ ...DEFAULT_TEMPLATE_FILTERS });
    clearFilters(FILTER_KEY);
    setToDate(null);
    setSearchQuery("");
    setSearchTemplateId("");
    setDateError(null);
    setController((prev) => ({ ...prev, page: 0, serverSidePage: 0 }));
  };

  const handleSync = async () => {
    setSyncFlag(true);
    setController((prev) => ({
      ...prev,
      page: 0,
      rowsPerPage: 20,
      serverSidePage: 0,
    }));
  };

  const getTotalActiveFilters = () => {
    let count = 0;
    if (languageFilter) count++;
    if (categoryFilter) count++;
    if (dateRange[0]) count++;
    if (dateRange[1]) count++;
    return count;
  };

  useEffect(() => {
    saveFilters(FILTER_KEY, filters);
  }, [filters]);

  const handleCampaignClick = (templateName, templateCategory, templateId) => {
    props.onNavigate("marketing_broadcast", {
      fromTemplatesTable: true,
      templateName: templateName,
      templateCategory: templateCategory,
      templateId: templateId,
    });
  };

  const tableColumns = [
    {
      id: "template_name",
      label: "Template Name",
      render: (template) => {
        const isInactive = template.active === false;
        if (isInactive) {
          return (
            <Tooltip title="This template has been disabled">
              <span style={{ color: "#9e9e9e" }}>{template.template_name}</span>
            </Tooltip>
          );
        }
        return (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleViewClick(template, e);
            }}
            style={{
              color: ColorsConstants.primaryBlue100,
              textDecoration: "none",
            }}
          >
            {template.template_name}
          </a>
        );
      },
    },
    {
      id: "category",
      label: "Category",
      render: (t) =>
        t.category === "MARKETING"
          ? "Marketing"
          : t.category === "UTILITY"
          ? "Utility"
          : "Unknown",
    },
    {
      id: "language",
      label: "Language",
      render: (t) => captilazeFirstLetter(t.language),
    },
    {
      id: "status",
      label: "Status",
      render: (template) => {
        const isInactive = template.active === false;
        if (isInactive) {
          return (
            <Chip
              variant="outlined"
              style={{ color: "#9e9e9e", borderColor: "#9e9e9e" }}
              label="INACTIVE"
              size="small"
            />
          );
        }
        if (template.status === "APPROVED")
          return <Chip variant="outlined" color="success" label="APPROVED" size="small" />;
        if (template.status === "REJECTED")
          return <Chip variant="outlined" color="primary" label="REJECTED" size="small" />;
        if (template.status === "FAILED")
          return <Chip variant="outlined" color="warning" label="FAILED" size="small" />;
        return <Chip variant="outlined" color="warning" label="PENDING" size="small" />;
      },
    },
    {
      id: "template_id",
      label: "Template ID",
      render: (t) =>
        t.event_id === "" || !t.event_id ? t.template_id : t.event_id,
    },
    {
      id: "template_quality",
      label: "Template Quality",
      render: (template) => {
        const q = template.template_quality;
        if (q === "UNKNOWN" || q === null) {
          if (["REJECTED", "FAILED"].includes(template.status)) return "";
          return (
            <Chip variant="outlined" color="success" size="small" label="Pending Quality" />
          );
        }
        const dotColor = { GREEN: "#10b981", YELLOW: "#f59e0b", RED: "#C40C0C" }[q];
        const textColor =
          { GREEN: "#059669", YELLOW: "#d97706", RED: "#C40C0C" }[q] ?? "text.primary";
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <DotIcon sx={{ fontSize: 8, color: dotColor }} />
            <Typography
              variant="body1"
              sx={{ fontWeight: 700, color: textColor, textTransform: "capitalize" }}
            >
              {template.template_quality?.toLowerCase() || "UNKNOWN"}
            </Typography>
          </Box>
        );
      },
    },
    { id: "description", label: "Description", render: (t) => t.description },
    {
      id: "created_at",
      label: "Created Date",
      render: (t) => formatWithTzLabel(t.created_at),
    },
    {
      id: "updated_at",
      label: "Updated At",
      render: (t) => formatWithTzLabel(t.updated_at),
    },
    { id: "updated_by", label: "Updated By", render: (t) => t.updated_by },
    {
      id: "action",
      label: "Action",
      align: "center",
      minWidth: 150,
      render: (template) => {
        const isInactive = template.active === false;
        if (isInactive) {
          return (
            <Tooltip title="This template has been disabled">
              <span>
                <IconButton disabled><EditIcon /></IconButton>
                <IconButton disabled style={{ marginLeft: 3 }}><DoDisturbIcon /></IconButton>
              </span>
            </Tooltip>
          );
        }
        return (
          <>
            <Tooltip title={"Broadcast Template"} arrow placement="top">
              <span>
                <IconButton
                  onClick={() =>
                    handleCampaignClick(
                      template.template_name,
                      template.category,
                      template.template_id
                    )
                  }
                  sx={{
                    color: "#0F172A",
                    display: template.status === "APPROVED" ? "inline-flex" : "none",
                  }}
                  disabled={template.status !== "APPROVED"}
                >
                  <CampaignIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={
                !(template.status === "APPROVED" || template.status === "REJECTED")
                  ? "Only approved and rejected templates can be edited"
                  : template.is_drip_template
                  ? "Active Drip templates cannot be edited"
                  : template?.template_preview_json?.marketingOption === "carousel"
                  ? "Image and Video Carousel templates cannot be edited"
                  : "Edit template"
              }
              arrow
              placement="top"
            >
              <span>
                <IconButton
                  onClick={() => {
                    setComponent(3);
                    setTemplatesData(template);
                  }}
                  sx={{ color: "#0F172A" }}
                  disabled={
                    dayjs().diff(dayjs(template.last_edited), "hour") < 24 ||
                    !(template.status === "APPROVED" || template.status === "REJECTED") ||
                    template.is_drip_template ||
                    template?.template_preview_json?.marketingOption === "carousel"
                  }
                  style={{
                    pointerEvents:
                      dayjs().diff(dayjs(template.last_edited), "hour") < 24 ||
                      !(template.status === "APPROVED" || template.status === "REJECTED") ||
                      template.is_drip_template
                        ? "auto"
                        : undefined,
                  }}
                >
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={
                template.is_drip_template
                  ? "Active Drip templates cannot be disabled"
                  : "Disable template"
              }
              arrow
              placement="top"
            >
              <span>
                <IconButton
                  onClick={() => handleDisableClick(template)}
                  style={{ marginLeft: 3 }}
                  sx={{ color: "#0F172A" }}
                  disabled={template.is_drip_template}
                >
                  <DoDisturbIcon />
                </IconButton>
              </span>
            </Tooltip>
          </>
        );
      },
    },
  ];

  const rowSx = (template, index) =>
    template.active === false
      ? {
          backgroundColor: "#f5f5f5",
          opacity: 0.7,
          cursor: "not-allowed",
          "&:hover": { backgroundColor: "#f5f5f5" },
        }
      : {
          backgroundColor: index % 2 === 0 ? "#F0F5FF" : "#FFFFFF",
          cursor: "default",
          "&:hover": { backgroundColor: index % 2 === 0 ? "#F5F5F5" : "#F7F9FC" },
          "& td": { borderBottom: "none", borderTop: "none" },
        };

  return (
    <div>
      {isMobile && (
        <Box sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <ButtonOne variant="contained" startIcon={<Add />} title="Create" />
          </Box>
          <Divider />
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
            <TableSearchInput
              searchQuery={searchQuery}
              onSearchChange={handleSearchQueryChange}
              charCount={charCount}
              tooltip="Search by Template Name"
            />
            <TableFilterButton
              setFilterModalOpen={setFilterModalOpen}
              totalActiveFilters={getTotalActiveFilters()}
            />
          </Box>
        </Box>
      )}

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        {!isMobile && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              sx={{
                p: 1,
                bgcolor: "background.paper",
                borderRadius: 1,
                my: 2,
              }}
            >
              <Grid container spacing={1} alignItems="end" wrap="wrap">

                <Grid item sx={{ flex: "0 0 17.5%" }}>
                  <InputLabel sx={{ mb: 1, color: "#232323" }}>Language</InputLabel>
                  <FormControl fullWidth size="small">
                    <Select
                      value={languageFilter || ""}
                      onChange={handleLanguageFilterChange}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return <span style={{ color: "#232323" }}>Language</span>;
                        }
                        return selected;
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                        borderRadius: "8px",
                      }}
                    >
                      <MenuItem value="" disabled>
                        English
                      </MenuItem>
                      {languageOptions}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item sx={{ flex: "0 0 17.5%" }}>
                  <InputLabel sx={{ mb: 1, color: "#232323" }}>
                    Search by Template Name
                  </InputLabel>
                  <Tooltip title="Search by Template Name" placement="top" arrow>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search by Template Name"
                      value={searchQuery}
                      onChange={handleSearchQueryChange}
                    />
                  </Tooltip>
                </Grid>

                <Grid item sx={{ flex: "0 0 17.5%" }}>
                  <InputLabel sx={{ mb: 1, color: "#232323" }}>
                    Search by Template Id
                  </InputLabel>
                  <Tooltip title="Search by Template Id" placement="top" arrow>
                    <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by Template Id"
                    value={searchTemplateId}
                    onChange={(e) => {
                      setSearchTemplateId(e.target.value);
                      }}
                      />
                  </Tooltip>
                </Grid>
                <Grid item sx={{ flex: "0 0 17.5%" }}>
                  <InputLabel sx={{ mb: 1, color: "#232323" }}>Status</InputLabel>
                  <FormControl fullWidth size="small">
                    <Select
                      value={statusFilter || ""}
                      onChange={(e) => updateFilter("status", e.target.value)}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected)
                          return <span style={{ color: "#232323" }}>Status</span>;
                        if (selected === "INACTIVE") return "Inactive";
                        return selected.charAt(0) + selected.slice(1).toLowerCase();
                      }}
                      sx={{ borderRadius: "8px" }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="APPROVED">Approved</MenuItem>
                      <MenuItem value="REJECTED">Rejected</MenuItem>
                      <MenuItem value="FAILED">Failed</MenuItem>
                      <MenuItem value="PENDING">Pending</MenuItem>
                      <MenuItem value="INACTIVE">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item sx={{ flex: "0 0 17.5%" }}>
                  <InputLabel sx={{ mb: 1 }}>Created Date From</InputLabel>
                  <DatePicker
                    disableFuture
                    format="DD/MM/YYYY"
                    value={dateRange[0]}
                    onChange={(newValue) => {
                      updateFilter("dateRange", [newValue, dateRange[1]]);
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        sx: {
                          "& .MuiInputBase-root": {
                            height: "40px",
                            borderRadius: "8px",
                          },
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid item sx={{ flex: "0 0 17.5%" }}>
                  <InputLabel sx={{ mb: 1, color: "#232323" }}>
                    Created Date To
                  </InputLabel>
                  <DatePicker
                    disableFuture
                    format="DD/MM/YYYY"
                    value={dateRange[1]}
                    minDate={dateRange[0] || undefined}
                    onChange={(newValue) => {
                      updateFilter("dateRange", [newValue, dateRange[1]]);
                    }}
                    sx={{ borderRadius: "8px" }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        error: (dateRange[1] && !dateRange[0]) || dateError,
                        helperText:
                          dateRange[1] && !dateRange[0]
                            ? "Please select Created Date From first"
                            : dateError,
                        sx: {
                          "& .MuiInputBase-root": {
                            height: "40px",
                            borderRadius: "8px",
                          },
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid item sx={{ flex: "0 0 12%" }}>
                  <ButtonTwo fullWidth title="Reset" onClick={handleResetFilter} />
                </Grid>

              </Grid>
            </Box>
          </LocalizationProvider>
      )}
        
      <AppTable
        columns={tableColumns}
        rows={templatesData}
        rowKey={(t, i) => t.template_id ?? i}
        emptyMessage="No match found"
        rowSx={rowSx}
        paperSx={{ border: "none", boxShadow: "none" }}
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
        
      <WhatsappFormDialog
        modalState={templatesModalState}
        handleModalState={settemplatesModalState}
        selectedClient={templatesModalState.selectedClient}
        />
      </Paper>

      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onReset={handleResetFilter}
      >
        <FilterModalItem
          name="Language"
          data={[...new Set(languageList)]}
          value={languageFilter}
          icon={<Translate />}
          onChange={handleLanguageFilterChange}
        />
        <FilterModalItem
          name="Category"
          data={["marketing", "utility"]}
          value={categoryFilter}
          icon={<Category />}
          onChange={handleCategoryFilterChange}
        />
        <FilterModalItem
          name="Created Date From"
          type="date"
          value={dateRange[0]}
          onChange={(newValue) => {
            updateFilter("dateRange", [newValue, dateRange[1]]);
            handleDateRangeApply();
          }}
          icon={<CalendarIcon />}
        />
        <FilterModalItem
          name="Created Date To"
          type="date"
          value={dateRange[1]}
          onChange={(newValue) => {
            updateFilter("dateRange", [dateRange[0], newValue]);
            handleDateRangeApply();
          }}
          icon={<CalendarIcon />}
        />
      </FilterModal>
    </div>
  );
}
